import React, { useState, useMemo, useEffect, useContext, useRef } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import CreatableSelect from 'react-select/creatable';
import { useTranslation } from 'react-i18next';
import { createExpense, updateExpense, createSupplier, updateSupplier, createExpenseCategory } from '../services/api';
import { trackAction } from '../services/analytics';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import CurrencyInput from './CurrencyInput';
import useIsMobile from '../hooks/useIsMobile';
import AuthContext from '../context/AuthContext';
import {
    BUILTIN_EXPENSE_CATEGORY_KEYS,
    buildExpenseCategoryOptions,
    formatExpenseCategory,
} from '../utils/expenseCategories';

const CreateExpenseModal = ({
    show,
    onHide,
    suppliers,
    expenseCategories = [],
    onCategoryCreated,
    onExpenseCreated,
    onExpenseUpdated,
    expenseToEdit = null,
    business = null,
    isCashRegisterOpen = true, // Default to true to not block if not provided
    onOpenCashRegister = null // Callback to open cash register modal
}) => {
    const { user } = useContext(AuthContext);
    const { t } = useTranslation();
    const isMobile = useIsMobile();

    const getLocalISODate = () => {
        const date = new Date();
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const initialExpenseModalState = {
        category: '',
        amount: '',
        date: getLocalISODate(),
        description: '',
        payment_method: 'Cash',
        supplier_id: '',
        supplier_name: '',
        supplier_phone: '',
        supplier_address: '',
        supplier_tax_id: '',
        is_deductible: false
    };

    const [expenseModalData, setExpenseModalData] = useState(initialExpenseModalState);
    const [supplierInputValue, setSupplierInputValue] = useState('');
    const justSelectedRef = useRef(false);
    const [saveSupplierData, setSaveSupplierData] = useState(false);
    const [existingSupplierData, setExistingSupplierData] = useState(null);
    const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);
    const [pendingExpenseData, setPendingExpenseData] = useState(null); // Stores expense data when waiting for cash register
    const [creatingCategory, setCreatingCategory] = useState(false);

    // Check if supplier settlements feature is enabled
    const hasSupplierSettlements = business?.plan_features?.supplier_settlements || false;

    // react-select no hereda el look de Bootstrap: sin esto, el placeholder largo de Categoría
    // se envuelve en 2 líneas dentro de la columna de medio ancho y el control queda más alto
    // que el resto de los campos del modal (Fecha, Método de Pago).
    const compactSelectStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: 38,
            borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
            boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13,110,253,0.25)' : 'none',
            '&:hover': { borderColor: state.isFocused ? '#86b7fe' : '#ced4da' },
        }),
        valueContainer: (base) => ({ ...base, padding: '2px 12px' }),
        placeholder: (base) => ({
            ...base,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    };

    const supplierOptions = useMemo(() => suppliers.map(s => ({
        value: s.supplier_id,
        label: s.name,
        phone: s.phone,
        address: s.address,
        tax_id: s.tax_id
    })), [suppliers]);

    // Categoría: 10 base traducidas + personalizadas activas del negocio.
    const categoryOptions = useMemo(
        () => buildExpenseCategoryOptions(expenseCategories, t),
        [expenseCategories, t]
    );

    // Opción seleccionada. Si el valor guardado no está en la lista (categoría desactivada,
    // valor histórico, o recién tecleado) se sintetiza para que el select lo muestre igual.
    const selectedCategoryOption = useMemo(() => {
        const value = expenseModalData.category;
        if (!value) return null;
        return categoryOptions.find(o => o.value === value)
            || { value, label: formatExpenseCategory(value, t) };
    }, [expenseModalData.category, categoryOptions, t]);

    const handleCategoryChange = async (newValue, actionMeta) => {
        if (!newValue) {
            setExpenseModalData(d => ({ ...d, category: '' }));
            return;
        }
        if (actionMeta.action === 'select-option') {
            setExpenseModalData(d => ({ ...d, category: newValue.value }));
            return;
        }
        if (actionMeta.action === 'create-option') {
            const name = (newValue.value || '').trim().slice(0, 50);
            if (!name) return;
            // Guardamos ya (backend permisivo); la categoría se persiste en paralelo.
            setExpenseModalData(d => ({ ...d, category: name }));
            setCreatingCategory(true);
            try {
                await createExpenseCategory({ name });
                onCategoryCreated?.();
                toast.success(t('expensesPage.toasts.categoryCreated', 'Categoría creada'));
            } catch (err) {
                const code = err.response?.data?.code;
                if (code === 'CATEGORY_EXISTS') {
                    onCategoryCreated?.();
                } else if (code === 'CATEGORY_RESERVED') {
                    const builtinKey = BUILTIN_EXPENSE_CATEGORY_KEYS.find(
                        k => k.toLowerCase() === name.toLowerCase()
                    );
                    if (builtinKey) setExpenseModalData(d => ({ ...d, category: builtinKey }));
                } else {
                    toast.error(t('expensesPage.toasts.categoryCreateFailed', 'No se pudo crear la categoría'));
                }
            } finally {
                setCreatingCategory(false);
            }
        }
    };

    const handleExpenseModalChange = (e) => {
        setExpenseModalData({ ...expenseModalData, [e.target.name]: e.target.value });
    };

    // Handle phone or tax_id blur to check for duplicates
    const handleSupplierFieldBlur = (fieldName) => {
        if (!saveSupplierData) return;
        if (expenseModalData.supplier_id) return; // Skip if already selected existing supplier

        const fieldValue = expenseModalData[fieldName]?.trim();
        if (!fieldValue) return;

        let existing = null;
        if (fieldName === 'supplier_phone') {
            existing = suppliers.find(s => s.phone === fieldValue);
        } else if (fieldName === 'supplier_tax_id') {
            existing = suppliers.find(s => s.tax_id === fieldValue);
        }

        if (existing) {
            // Check if there are changes
            const hasChanges =
                existing.name !== expenseModalData.supplier_name?.trim() ||
                existing.phone !== expenseModalData.supplier_phone?.trim() ||
                existing.address !== expenseModalData.supplier_address?.trim() ||
                existing.tax_id !== expenseModalData.supplier_tax_id?.trim();

            if (hasChanges) {
                setExistingSupplierData(existing);
                setShowUpdateConfirm(true);
            }
        }
    };

    const handleUpdateSupplier = async () => {
        if (!existingSupplierData) return;
        try {
            await updateSupplier(existingSupplierData.supplier_id, {
                name: expenseModalData.supplier_name,
                phone: expenseModalData.supplier_phone,
                address: expenseModalData.supplier_address,
                tax_id: expenseModalData.supplier_tax_id
            });
            setShowUpdateConfirm(false);
            setExistingSupplierData(null);
            toast.success(t('expensesPage.toasts.supplierUpdated', 'Datos del proveedor actualizados'));
        } catch (error) {
            console.error('Error updating supplier:', error);
            toast.error(t('expensesPage.toasts.supplierUpdateFailed', 'Error al actualizar proveedor'));
        }
    };

    const handleContinueWithoutUpdate = () => {
        setShowUpdateConfirm(false);
        setExistingSupplierData(null);
        // Continue with the entered data without updating the supplier in DB
    };

    const handleSupplierChange = (newValue, actionMeta) => {
        if (newValue) {
            justSelectedRef.current = true;
            if (actionMeta.action === 'select-option') {
                setExpenseModalData({
                    ...expenseModalData,
                    supplier_id: newValue.value,
                    supplier_name: newValue.label,
                    supplier_phone: newValue.phone || '',
                    supplier_address: newValue.address || '',
                    supplier_tax_id: newValue.tax_id || ''
                });
            } else if (actionMeta.action === 'create-option') {
                setExpenseModalData({
                    ...expenseModalData,
                    supplier_id: '',
                    supplier_name: newValue.value,
                    supplier_phone: '',
                    supplier_address: '',
                    supplier_tax_id: ''
                });
            }
        } else {
            setExpenseModalData({
                ...expenseModalData,
                supplier_id: '',
                supplier_name: '',
                supplier_phone: '',
                supplier_address: '',
                supplier_tax_id: ''
            });
        }
    };

    const handleSupplierBlur = () => {
        if (isMobile) return;
        const inputValue = supplierInputValue;
        setTimeout(() => {
            if (justSelectedRef.current) {
                justSelectedRef.current = false;
                return;
            }
            if (inputValue && !expenseModalData.supplier_id && expenseModalData.supplier_name !== inputValue) {
                const match = supplierOptions.find(opt => opt.label.toLowerCase() === inputValue.toLowerCase());
                if (match) {
                    setExpenseModalData({
                        ...expenseModalData,
                        supplier_id: match.value,
                        supplier_name: match.label,
                        supplier_phone: match.phone || '',
                        supplier_address: match.address || '',
                        supplier_tax_id: match.tax_id || ''
                    });
                } else {
                    setExpenseModalData({
                        ...expenseModalData,
                        supplier_id: '',
                        supplier_name: inputValue
                    });
                }
            }
        }, 200);
    };

    // Load expense data when editing
    useEffect(() => {
        if (expenseToEdit) {
            // Format date properly from UTC
            const formattedDate = expenseToEdit.date ? new Date(expenseToEdit.date).toISOString().split('T')[0] : getLocalISODate();

            setExpenseModalData({
                ...expenseToEdit,
                date: formattedDate,
                supplier_id: expenseToEdit.supplier_id || '',
                supplier_name: expenseToEdit.supplier_name || '',
                supplier_phone: expenseToEdit.supplier_phone || '',
                supplier_address: expenseToEdit.supplier_address || '',
                supplier_tax_id: expenseToEdit.supplier_tax_id || '',
                is_deductible: expenseToEdit.is_deductible || false
            });
        } else {
            setExpenseModalData(initialExpenseModalState);
        }
        setSupplierInputValue('');
        setSaveSupplierData(false);
    }, [expenseToEdit, show]);

    // Function to actually save the expense
    const saveExpense = async (data) => {
        const isEditing = !!expenseToEdit;
        const toastId = toast.loading(isEditing ? t('expensesPage.toasts.updating') : t('expensesPage.toasts.creating'));
        try {
            if (isEditing) {
                await updateExpense(expenseToEdit.expense_id, data);
                // Telemetría (tarea 004): acción clave sin PII (solo booleanos).
                trackAction('expense_updated', { is_deductible: !!data.is_deductible });
                toast.success(t('expensesPage.toasts.updated'), { id: toastId });
                if (onExpenseUpdated) {
                    onExpenseUpdated();
                }
            } else {
                await createExpense(data);
                trackAction('expense_created', { is_deductible: !!data.is_deductible });
                toast.success(t('expensesPage.toasts.created'), { id: toastId });
                if (onExpenseCreated) {
                    onExpenseCreated();
                }

                // Save supplier data if checkbox is checked and it's a new expense
                if (saveSupplierData && !data.supplier_id && data.supplier_name?.trim()) {
                    try {
                        await createSupplier({
                            name: data.supplier_name,
                            phone: data.supplier_phone,
                            address: data.supplier_address,
                            tax_id: data.supplier_tax_id
                        });
                        toast.success(t('expensesPage.toasts.supplierSaved', 'Proveedor guardado exitosamente'));
                    } catch (error) {
                        console.error('Error saving supplier:', error);
                        toast.error(t('expensesPage.toasts.supplierSaveFailed', 'No se pudo guardar el proveedor'));
                    }
                }
            }
            setExpenseModalData(initialExpenseModalState);
            setSupplierInputValue('');
            setSaveSupplierData(false);
            setPendingExpenseData(null);
            onHide();
        } catch (error) {
            trackAction('expense_save_failed', { is_edit: isEditing });
            toast.error(isEditing ? t('expensesPage.toasts.updateFailed') : t('expensesPage.toasts.createFailed'), { id: toastId });
        }
    };

    // Public method that can be called after cash register opens
    const processPendingExpense = async () => {
        if (pendingExpenseData) {
            await saveExpense(pendingExpenseData);
        }
    };

    const handleExpenseModalSubmit = async (e) => {
        e.preventDefault();
        const isEditing = !!expenseToEdit;

        // react-select no soporta `required` nativo — validamos aquí también.
        if (!expenseModalData.category?.trim()) {
            toast.error(t('expensesPage.toasts.categoryRequired', 'La categoría es obligatoria'));
            return;
        }

        // Check if cash register is open (only for new expenses, not edits)
        if (!isEditing && !isCashRegisterOpen && onOpenCashRegister) {
            const result = await Swal.fire({
                title: t('cashRegisterPage.autoOpen.title', 'Caja No Abierta'),
                text: t('cashRegisterPage.autoOpen.message', 'La caja registradora no está abierta. ¿Desea abrirla antes de continuar?'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: t('cashRegisterPage.autoOpen.openButton', 'Abrir Caja'),
                cancelButtonText: t('cashRegisterPage.autoOpen.continueWithout', 'Continuar sin abrir'),
                confirmButtonColor: 'var(--ev-success-solid)',
                cancelButtonColor: 'var(--ev-bg-subtle)'
            });

            if (result.isConfirmed) {
                // Store pending expense and trigger cash register modal
                setPendingExpenseData({ ...expenseModalData });
                onOpenCashRegister({ pendingCallback: processPendingExpense });
                return;
            }
            // If cancelled, continue saving without opening cash register
        }

        // Save directly
        await saveExpense(expenseModalData);
    };

    const handleClose = () => {
        setExpenseModalData(initialExpenseModalState);
        setSupplierInputValue('');
        setSaveSupplierData(false);
        onHide();
    };

    return (
        <>
            <Modal show={show} onHide={handleClose} backdrop={isMobile ? 'static' : true}>
                <Modal.Header closeButton>
                    <Modal.Title>{expenseToEdit ? t('expensesPage.modal.editTitle') : t('expensesPage.modal.createTitle')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleExpenseModalSubmit}>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('expensesPage.modal.dateLabel')}</Form.Label>
                                    <Form.Control type="date" name="date" value={expenseModalData.date} onChange={handleExpenseModalChange} required />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('expensesPage.modal.categoryLabel')}</Form.Label>
                                    <CreatableSelect
                                        isClearable
                                        isDisabled={creatingCategory}
                                        options={categoryOptions}
                                        value={selectedCategoryOption}
                                        onChange={handleCategoryChange}
                                        placeholder={t('expensesPage.modal.categoryPlaceholder', 'Seleccione o escriba una categoría')}
                                        formatCreateLabel={(input) => t('expensesPage.modal.categoryCreateLabel', { value: input, defaultValue: `Crear "${input}"` })}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={compactSelectStyles}
                                    />
                                    {/* react-select no expone `required`; input espejo para la validación nativa del form */}
                                    <input
                                        tabIndex={-1}
                                        aria-hidden="true"
                                        required
                                        value={expenseModalData.category || ''}
                                        onChange={() => {}}
                                        style={{ opacity: 0, height: 0, width: 0, position: 'absolute' }}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('expensesPage.modal.paymentMethodLabel')}</Form.Label>
                                    <Form.Select name="payment_method" value={expenseModalData.payment_method} onChange={handleExpenseModalChange}>
                                        <option value="Cash">{t('expensesPage.paymentMethods.Cash')}</option>
                                        <option value="Transfer">{t('expensesPage.paymentMethods.Transfer')}</option>
                                        <option value="Card">{t('expensesPage.paymentMethods.Card')}</option>
                                        <option value="Nequi">{t('expensesPage.paymentMethods.Nequi')}</option>
                                        <option value="Daviplata">{t('expensesPage.paymentMethods.Daviplata')}</option>
                                        <option value="Other">{t('expensesPage.paymentMethods.Other')}</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('expensesPage.modal.amountLabel')}</Form.Label>
                                    <CurrencyInput
                                        value={expenseModalData.amount}
                                        onChange={(value) => setExpenseModalData({ ...expenseModalData, amount: value })}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('expensesPage.modal.descriptionLabel')}</Form.Label>
                            <Form.Control as="textarea" rows={3} name="description" value={expenseModalData.description} onChange={handleExpenseModalChange} />
                        </Form.Group>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Proveedor</Form.Label>
                                    <CreatableSelect
                                        isClearable
                                        options={supplierOptions}
                                        onChange={handleSupplierChange}
                                        onInputChange={(val) => setSupplierInputValue(val)}
                                        onBlur={handleSupplierBlur}
                                        value={expenseModalData.supplier_id ? supplierOptions.find(opt => opt.value === expenseModalData.supplier_id) : (expenseModalData.supplier_name ? { label: expenseModalData.supplier_name, value: expenseModalData.supplier_name } : null)}
                                        placeholder="Seleccione o escriba un proveedor"
                                        formatCreateLabel={(inputValue) => `Crear "${inputValue}"`}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        {/* Checkbox to save supplier data */}
                        {!expenseModalData.supplier_id && expenseModalData.supplier_name && (
                            <div className="mb-3 p-3 rounded d-flex align-items-center" style={{ backgroundColor: 'var(--ev-bg-brand-subtle)' }}>
                                <Form.Check
                                    type="checkbox"
                                    id="save-supplier-data"
                                    label={<span className="fw-bold text-dark">{t('expensesPage.modal.saveSupplierData', 'Guardar datos del proveedor')}</span>}
                                    checked={saveSupplierData}
                                    onChange={(e) => setSaveSupplierData(e.target.checked)}
                                    className="mb-0 w-100"
                                />
                            </div>
                        )}
                        {expenseModalData.supplier_name && (
                            <>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Nombre del Proveedor</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="supplier_name"
                                                value={expenseModalData.supplier_name}
                                                onChange={handleExpenseModalChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>NIT / CC</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="supplier_tax_id"
                                                value={expenseModalData.supplier_tax_id}
                                                onChange={handleExpenseModalChange}
                                                onBlur={() => handleSupplierFieldBlur('supplier_tax_id')}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Teléfono</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="supplier_phone"
                                                value={expenseModalData.supplier_phone}
                                                onChange={handleExpenseModalChange}
                                                onBlur={() => handleSupplierFieldBlur('supplier_phone')}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Dirección</Form.Label>
                                            <Form.Control type="text" name="supplier_address" value={expenseModalData.supplier_address} onChange={handleExpenseModalChange} />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </>
                        )}
                        {hasSupplierSettlements && (
                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="checkbox"
                                    id="is_deductible"
                                    name="is_deductible"
                                    label={t('expensesPage.modal.isDeductibleLabel')}
                                    checked={expenseModalData.is_deductible}
                                    onChange={(e) => setExpenseModalData({ ...expenseModalData, is_deductible: e.target.checked })}
                                    disabled={!expenseModalData.supplier_id && !expenseModalData.supplier_name}
                                />
                                <Form.Text className="text-muted">
                                    {t('expensesPage.modal.isDeductibleHelp')}
                                </Form.Text>
                            </Form.Group>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleClose}>{t('cancel')}</Button>
                        <Button variant="primary" type="submit">
                            {t('expensesPage.modal.saveButton')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Supplier Update Confirmation Modal */}
            <Modal show={showUpdateConfirm} onHide={handleContinueWithoutUpdate} backdrop={isMobile ? 'static' : true}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('expensesPage.modal.existingSupplier', 'Proveedor Existente')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{t('expensesPage.modal.existingSupplierMessage', 'Ya existe un proveedor registrado con estos datos:')}</p>
                    {existingSupplierData && (
                        <div className="mb-3 p-3 bg-light rounded">
                            <strong>{existingSupplierData.name}</strong><br />
                            {existingSupplierData.phone && <><small>Tel: {existingSupplierData.phone}</small><br /></>}
                            {existingSupplierData.tax_id && <><small>NIT: {existingSupplierData.tax_id}</small><br /></>}
                            {existingSupplierData.address && <small>Dir: {existingSupplierData.address}</small>}
                        </div>
                    )}
                    <p>{t('expensesPage.modal.updateSupplierQuestion', '¿Deseas actualizar los datos del proveedor con la nueva información?')}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleContinueWithoutUpdate}>
                        {t('expensesPage.modal.continueWithoutUpdate', 'Continuar sin actualizar')}
                    </Button>
                    <Button variant="primary" onClick={handleUpdateSupplier}>
                        {t('expensesPage.modal.updateSupplier', 'Actualizar Datos')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default CreateExpenseModal;
